import { z } from "@hono/zod-openapi";

import {
  DEFAULT_CHANGE_PAGE_SIZE,
  MAX_CHANGE_PAGE_SIZE,
  MAX_SNAPSHOT_ENTRIES,
  PROTOCOL_VERSION,
} from "./capabilities";
import { changeCursorSchema, uuidV7Schema } from "./identifiers";

const contentSha256Schema = z.string().regex(/^[0-9a-f]{64}$/u);

const snapshotEntryBase = {
  entry_id: uuidV7Schema,
  parent_entry_id: uuidV7Schema,
  name: z.string(),
  effective_path: z.string(),
  revision_id: uuidV7Schema,
};

export const snapshotFolderSchema = z.object(snapshotEntryBase);

export const snapshotFileSchema = z.object({
  ...snapshotEntryBase,
  content_sha256: contentSha256Schema,
  byte_length: z.number().int().nonnegative().max(1_900_000),
});

export const vaultSnapshotResponseSchema = z
  .object({
    protocol_version: z.literal(PROTOCOL_VERSION),
    vault: z.object({
      vault_id: uuidV7Schema,
      vault_type: z.enum(["company", "team", "personal"]),
      name: z.string(),
      local_sync_allowed: z.boolean(),
    }),
    root_entry_id: uuidV7Schema,
    high_water_cursor: changeCursorSchema,
    folders: z.array(snapshotFolderSchema).max(MAX_SNAPSHOT_ENTRIES),
    files: z.array(snapshotFileSchema).max(MAX_SNAPSHOT_ENTRIES),
  })
  .superRefine((snapshot, context) => {
    if (
      snapshot.folders.length + snapshot.files.length >
      MAX_SNAPSHOT_ENTRIES
    ) {
      context.addIssue({
        code: "custom",
        message: "snapshot_too_large",
      });
    }
  })
  .openapi("VaultSnapshotResponse");

const changeEventBase = {
  cursor: changeCursorSchema,
  entry_id: uuidV7Schema,
  revision_id: uuidV7Schema,
  parent_entry_id: uuidV7Schema,
  name: z.string(),
  effective_path: z.string(),
  created_at: z.iso.datetime(),
};

export const vaultChangeEventSchema = z.discriminatedUnion("entry_type", [
  z.object({
    ...changeEventBase,
    entry_type: z.literal("folder"),
    mutation_type: z.enum(["create", "rename", "move", "delete", "restore"]),
  }),
  z.object({
    ...changeEventBase,
    entry_type: z.literal("file"),
    mutation_type: z.enum([
      "create",
      "edit",
      "move",
      "delete",
      "restore",
      "resolve",
    ]),
    content_sha256: contentSha256Schema.nullable(),
    byte_length: z.number().int().nonnegative().max(1_900_000).nullable(),
  }),
]);

export const vaultChangesResponseSchema = z
  .object({
    protocol_version: z.literal(PROTOCOL_VERSION),
    events: z.array(vaultChangeEventSchema).max(MAX_CHANGE_PAGE_SIZE),
    next_cursor: changeCursorSchema,
    has_more: z.boolean(),
  })
  .openapi("VaultChangesResponse");

const decimalIntegerSchema = z
  .string()
  .regex(/^(?:0|[1-9][0-9]*)$/u)
  .transform(Number)
  .refine(Number.isSafeInteger);

export const vaultChangesQuerySchema = z.object({
  after: decimalIntegerSchema,
  limit: decimalIntegerSchema
    .refine((value) => value >= 1 && value <= MAX_CHANGE_PAGE_SIZE)
    .default(DEFAULT_CHANGE_PAGE_SIZE),
});

export type VaultSnapshotResponse = z.infer<typeof vaultSnapshotResponseSchema>;
export type VaultChangeEvent = z.infer<typeof vaultChangeEventSchema>;
export type VaultChangesResponse = z.infer<typeof vaultChangesResponseSchema>;
